import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Statistic,
  Progress,
  Tree,
  Tabs,
  Empty,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  ApartmentOutlined,
  DollarOutlined,
  WarningOutlined,
  UnorderedListOutlined,
  PartitionOutlined,
} from '@ant-design/icons';
import { costCenterService, userService } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CostCenters = () => {
  const [costCenters, setCostCenters] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'tree'
  const [form] = Form.useForm();
  const { can } = usePermissions();

  const canEdit = can('spend', 'edit');
  const canDelete = can('spend', 'delete');

  const fetchCostCenters = useCallback(async () => {
    setLoading(true);
    try {
      const [centersData, treeDataRes, statsData] = await Promise.all([
        costCenterService.getAll({ active_only: true }),
        costCenterService.getTree(),
        costCenterService.getStats(),
      ]);
      setCostCenters(centersData);
      setTreeData(treeDataRes);
      setStats(statsData);
    } catch (error) {
      message.error('Failed to load cost centers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, []);

  useEffect(() => {
    fetchCostCenters();
    fetchUsers();
  }, [fetchCostCenters, fetchUsers]);

  const handleCreateEdit = async (values) => {
    try {
      if (editing) {
        await costCenterService.update(editing.id, values);
        message.success('Cost center updated successfully');
      } else {
        await costCenterService.create(values);
        message.success('Cost center created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      setEditing(null);
      fetchCostCenters();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await costCenterService.delete(id);
      message.success('Cost center deleted successfully');
      fetchCostCenters();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete cost center');
    }
  };

  const openCreateModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      currency: 'USD',
      annual_budget: 0,
    });
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditing(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      description: record.description,
      parent_id: record.parent_id,
      manager_user_id: record.manager_user_id,
      annual_budget: record.annual_budget,
      currency: record.currency,
      gl_code: record.gl_code,
      department_code: record.department_code,
      external_id: record.external_id,
    });
    setModalVisible(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (code) => <Text strong>{code}</Text>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Manager',
      dataIndex: 'manager',
      key: 'manager',
      render: (manager) => manager?.full_name || manager?.email || '-',
    },
    {
      title: 'Annual Budget',
      dataIndex: 'annual_budget',
      key: 'annual_budget',
      sorter: (a, b) => (a.annual_budget || 0) - (b.annual_budget || 0),
      render: (budget) => formatCurrency(budget),
      align: 'right',
    },
    {
      title: 'GL Code',
      dataIndex: 'gl_code',
      key: 'gl_code',
      render: (code) => code ? <Tag>{code}</Tag> : '-',
    },
    {
      title: 'Dept Code',
      dataIndex: 'department_code',
      key: 'department_code',
      render: (code) => code ? <Tag color="blue">{code}</Tag> : '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canEdit && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title="Delete this cost center?"
              description="This will mark the cost center as inactive."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete"
              okType="danger"
            >
              <Tooltip title="Delete">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Convert tree data to Ant Design Tree format
  const convertToTreeFormat = (nodes) => {
    return nodes.map((node) => {
      const utilizationPercent = node.annual_budget > 0
        ? ((node.actual_spend / node.annual_budget) * 100).toFixed(1)
        : 0;
      const isOverBudget = node.actual_spend > node.annual_budget;

      return {
        key: node.id,
        title: (
          <Space size="large">
            <Text strong>{node.code}</Text>
            <Text>{node.name}</Text>
            <Text type="secondary">
              Budget: {formatCurrency(node.annual_budget)}
            </Text>
            <Text type={isOverBudget ? 'danger' : 'success'}>
              Spent: {formatCurrency(node.actual_spend)}
            </Text>
            <Progress
              percent={parseFloat(utilizationPercent)}
              size="small"
              style={{ width: 100 }}
              status={isOverBudget ? 'exception' : 'normal'}
            />
          </Space>
        ),
        children: node.children?.length > 0 ? convertToTreeFormat(node.children) : undefined,
      };
    });
  };

  const renderStats = () => {
    if (!stats) return null;

    const utilizationStatus = stats.budget_utilization_percentage > 90
      ? 'exception'
      : stats.budget_utilization_percentage > 75
        ? 'active'
        : 'normal';

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Cost Centers"
              value={stats.total_cost_centers}
              prefix={<ApartmentOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Annual Budget"
              value={stats.total_annual_budget}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="YTD Spend"
              value={stats.total_ytd_spend}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
            />
            <Progress
              percent={stats.budget_utilization_percentage.toFixed(1)}
              size="small"
              status={utilizationStatus}
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Over Budget"
              value={stats.cost_centers_over_budget}
              prefix={<WarningOutlined />}
              valueStyle={{ color: stats.cost_centers_over_budget > 0 ? '#cf1322' : '#3f8600' }}
            />
            {stats.unassigned_spend > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Unassigned: {formatCurrency(stats.unassigned_spend)}
              </Text>
            )}
          </Card>
        </Col>
      </Row>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      );
    }

    if (viewMode === 'tree') {
      if (treeData.length === 0) {
        return (
          <Empty
            description="No cost centers found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                Create First Cost Center
              </Button>
            )}
          </Empty>
        );
      }

      return (
        <Tree
          showLine={{ showLeafIcon: false }}
          showIcon
          defaultExpandAll
          treeData={convertToTreeFormat(treeData)}
          icon={({ expanded }) => expanded ? <FolderOpenOutlined /> : <FolderOutlined />}
        />
      );
    }

    return (
      <Table
        columns={columns}
        dataSource={costCenters}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    );
  };

  const tabItems = [
    {
      key: 'list',
      label: (
        <span>
          <UnorderedListOutlined />
          List View
        </span>
      ),
    },
    {
      key: 'tree',
      label: (
        <span>
          <PartitionOutlined />
          Hierarchy View
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {renderStats()}

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>Cost Centers</Title>
          <Space>
            <Tabs
              activeKey={viewMode}
              onChange={setViewMode}
              items={tabItems}
              size="small"
              style={{ marginBottom: 0 }}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchCostCenters}>
              Refresh
            </Button>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                Add Cost Center
              </Button>
            )}
          </Space>
        </div>

        {renderContent()}
      </Card>

      <Modal
        title={editing ? 'Edit Cost Center' : 'Create Cost Center'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditing(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateEdit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Cost Center Code"
                rules={[{ required: true, message: 'Please enter a code' }]}
              >
                <Input placeholder="e.g., CC-ENG-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter a name' }]}
              >
                <Input placeholder="e.g., Engineering - Cloud Infrastructure" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="parent_id" label="Parent Cost Center">
                <Select
                  allowClear
                  placeholder="Select parent (optional)"
                  optionFilterProp="children"
                  showSearch
                >
                  {costCenters
                    .filter((cc) => !editing || cc.id !== editing.id)
                    .map((cc) => (
                      <Option key={cc.id} value={cc.id}>
                        {cc.code} - {cc.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="manager_user_id" label="Manager">
                <Select
                  allowClear
                  placeholder="Select manager (optional)"
                  optionFilterProp="children"
                  showSearch
                >
                  {users.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.full_name || user.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="annual_budget" label="Annual Budget">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currency" label="Currency">
                <Select>
                  <Option value="USD">USD - US Dollar</Option>
                  <Option value="EUR">EUR - Euro</Option>
                  <Option value="GBP">GBP - British Pound</Option>
                  <Option value="CAD">CAD - Canadian Dollar</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="gl_code" label="GL Code">
                <Input placeholder="e.g., 6100-01" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="department_code" label="Department Code">
                <Input placeholder="e.g., ENG" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="external_id" label="External ID (ERP)">
                <Input placeholder="e.g., SAP-CC-001" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editing ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CostCenters;
